import { Chunk, FindChunks } from 'react-highlight-words'
import { isString } from './assertions'

/**
 * A separator object containing some common separator characters.
 * @typedef {Object} Separator
 * @property {string} dash - A dash separator.
 */

const SEPARATOR = {
  /**
   * A dash separator.
   */
  dash: '-',
}

/**
 * Removes all spaces from a string.
 * If the input is not a string, it will be converted to a string first.
 *
 * @param {unknown} str - The string to remove spaces from.
 * @return {string} The input string with all spaces removed.
 */
const removeSpaces = (str: unknown = ''): string => {
  // Check if the input is a string
  if (isString(str)) {
    // Use the replace method to remove all spaces from the string
    return str.replace(/ /g, '')
  } else {
    // Convert the input to a string and remove all spaces
    return String(str).replace(/ /g, '')
  }
}
/**
 * Removes the plus sign from the start of a phone number.
 * @param {string} number - The phone number to remove the plus sign from.
 * @return {string} The phone number without the plus sign.
 */
const removePlus = (number: string): string => {
  if (!number.length) return ''
  const removed = number.replace('+', '')
  return removed
}
/**
 * matched character with its uppercase version.
 *
 * @param {string} s - The matched character.
 * @return {string} The uppercase version of the matched character.
 */
const replace = (s: string): string => {
  return s.toUpperCase()
}

/**
 * Truncates a string to a specified length and appends an ellipsis if necessary.
 *
 * @param {string} str - The string to truncate.
 * @param {number} num - The maximum length of the truncated string.
 * @return {string} The truncated string with an ellipsis if necessary.
 */
const truncate = (str: string = '', num: number): string => {
  // If the length of the string is less than or equal to the specified maximum length,
  // return the string as is.
  if (str.length <= num) {
    return str
  }

  // If the length of the string is greater than the specified maximum length,
  // return a truncated string with an ellipsis appended.
  return `${str.slice(0, num)}...`
}

/**
 * Capitalizes the first letter of each word in a string.
 *
 * @param {string} name - The string to capitalize. Defaults to an empty string.
 * @return {string} The capitalized string.
 */
const capitalizeName = (name: string = ''): string => {
  /**
   * This regular expression matches the first letter of each word in the string.
   * The \b matches word boundaries, and the \w matches any word character.
   * The parentheses create a capturing group, which allows us to access the matched
   * character in the replace function.
   */
  const regex = /\b(\w)/g

  // Replace each matched character with its uppercase version.
  return name.replace(regex, replace)
}

/**
 * Formats a string by replacing spaces with hyphens and converting to lowercase.
 *
 * @param {string} text - The string to format. Defaults to an empty string.
 * @return {string} The formatted string.
 */
const getTextInFormat = (text: string = ''): string => {
  return text.replace(/ /g, SEPARATOR.dash).toLowerCase()
}

/**
 * Finds all occurrences of search words in a given text and returns
 * an array of chunks representing the start and end indices of each occurrence.
 *
 * @param {FindChunks} params - An object containing the search words to find
 * and the text to search in.
 * @returns {Chunk[]} An array of chunks representing the start and end indices
 * of each occurrence of the search words in the text.
 */
const customFindChunks = ({
  searchWords,
  textToHighlight,
}: FindChunks): Chunk[] => {
  // Filter out empty search words
  const filteredSearchWords = searchWords.filter((searchWord) => searchWord)

  // Reduce the array of search words to a single array of chunks
  return filteredSearchWords.reduce((chunks, searchWord) => {
    // Create a regular expression pattern to match the search word
    const searchWordPattern = [...(searchWord as unknown as string[])]
      .map((char) => {
        // Escape special characters in the search word and add a whitespace
        // character after each character
        return `${char.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\s*`
      })
      .join('')

    // Create a regular expression with the search word pattern
    const regex = new RegExp(`(${searchWordPattern})`, 'gi')

    // Find all occurrences of the search word in the text
    let match
    while ((match = regex.exec(textToHighlight)) !== null) {
      const startIndex = match.index
      const endIndex = startIndex + match[0].length
      chunks.push({ start: startIndex, end: endIndex })

      // Prevent infinite loop by incrementing lastIndex
      if (match.index === regex.lastIndex) {
        regex.lastIndex++
      }
    }
    return chunks
  }, [] as Chunk[])
}

/**
 * Returns the full name or first name of an object.
 *
 * This function takes an object or an array of strings as an argument and
 * returns the full name or the first name of the object based on the `fullName`
 * flag. If the `fullName` flag is true, it returns the full name of the object.
 * If it is false, it returns the first name of the object. If the input object is
 * undefined or null, it returns an empty string.
 *
 * @param {Record<string, any> | string[] | undefined} obj - The object or an array of names.
 * @param {boolean} fullName - Whether to return the full name or just the first name.
 * @return {string} The full name or first name of the object.
 */
const getName = (
  obj: Record<string, any> | string[] | undefined,
  fullName = true,
): string => {
  // If the input object is undefined or null, return an empty string.
  if (!obj) {
    return ''
  }

  // If the input object is an array, filter out falsy values and handle based on fullName flag.
  if (Array.isArray(obj)) {
    const filteredNames = obj.filter(
      (part): part is string => typeof part === 'string',
    )
    if (fullName) {
      return filteredNames.join(' ')
    }
    return filteredNames[0] || ''
  }

  // If the input object is an object, handle its 'name' property.
  if (typeof obj === 'object' && obj !== null) {
    // Check if the input object directly has a 'name' property that is a string.
    if (typeof obj.name === 'string') {
      return fullName ? obj.name : obj.name.split(' ')[0]
    }

    // Check if the input object directly has a 'name' property that is an array of strings.
    if (Array.isArray(obj.name)) {
      const filteredNames = obj.name.filter(
        (part): part is string => typeof part === 'string',
      )
      if (fullName) {
        return filteredNames.join(' ')
      }
      return filteredNames[0] || ''
    }

    // Recursively search through the object's values to find a name.
    for (const value of Object.values(obj)) {
      if (typeof value === 'object' && value !== null) {
        const name = getName(value, fullName)
        if (name) {
          return name
        }
      }
    }
  }

  // Return empty string if no name is found.
  return ''
}

export {
  removeSpaces,
  truncate,
  capitalizeName,
  getTextInFormat,
  getName,
  customFindChunks,
  removePlus,
}
